import {
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData
} from '@tanstack/react-query';
import { handoverScanningApi } from './handover-scanning.api';
import type { WorkloadListParams, ReceivingListParams } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export const handoverScanningKeys = {
  all: ['handover-scanning'] as const,
  lists: () => [...handoverScanningKeys.all, 'list'] as const,
  list: (filters: WorkloadListParams) =>
    [...handoverScanningKeys.lists(), filters] as const,
  metrics: () => [...handoverScanningKeys.all, 'metrics'] as const,
  receivingLists: () => [...handoverScanningKeys.all, 'receiving'] as const,
  receivingList: (filters: ReceivingListParams) =>
    [...handoverScanningKeys.receivingLists(), filters] as const,
  receivingInfiniteLists: () =>
    [...handoverScanningKeys.all, 'receiving-infinite'] as const,
  receivingInfiniteList: (filters: ReceivingListParams) =>
    [...handoverScanningKeys.receivingInfiniteLists(), filters] as const,
  details: () => [...handoverScanningKeys.all, 'detail'] as const,
  detail: (id: number) => [...handoverScanningKeys.details(), id] as const
};

export const handoverScanningQueries = {
  // `scanned_pages` / `sla_progress` / `status` on an assignment advance as the
  // assigned operator scans in their own session — the mutations here only
  // cover assignment CRUD, so nothing marks progress stale in a supervisor's
  // tab. Same reasoning for `detail` and the two receiving-list variants, whose
  // containers are handed over by the upstream stage.
  list: (filters: WorkloadListParams = {}) =>
    queryOptions({
      queryKey: handoverScanningKeys.list(filters),
      queryFn: () => handoverScanningApi.getAll(filters),
      placeholderData: keepPreviousData,
      staleTime: STALE_TIME.live
    }),

  metrics: () =>
    queryOptions({
      queryKey: handoverScanningKeys.metrics(),
      queryFn: () => handoverScanningApi.getMetrics(),
      staleTime: STALE_TIME.metrics
    }),

  receivingList: (filters: ReceivingListParams = {}) =>
    queryOptions({
      queryKey: handoverScanningKeys.receivingList(filters),
      queryFn: () => handoverScanningApi.getReceivingList(filters),
      placeholderData: keepPreviousData,
      staleTime: STALE_TIME.live
    }),

  /**
   * Page-cursor infinite variant for the receiving-list card grid.
   * `filters` must not include `page` — the cursor owns it.
   */
  receivingInfiniteList: (filters: ReceivingListParams = {}) =>
    infiniteQueryOptions({
      queryKey: handoverScanningKeys.receivingInfiniteList(filters),
      queryFn: ({ pageParam }) =>
        handoverScanningApi.getReceivingList({ ...filters, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.hasNext ? lastPage.pagination.page + 1 : undefined,
      placeholderData: keepPreviousData,
      staleTime: STALE_TIME.live
    }),

  detail: (id: number) =>
    queryOptions({
      queryKey: handoverScanningKeys.detail(id),
      queryFn: () => handoverScanningApi.getOne(id),
      staleTime: STALE_TIME.live
    })
};
